import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import Header from '../public/Header';
import Footer from '../public/Footer';

const PaymentCallback = ({ onLogin }) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
    const [message, setMessage] = useState('');

    useEffect(() => {
        const subscriptionId = searchParams.get('subscriptionId');

        // Mollie redirects back after payment
        // The webhook handles the actual status update
        // Here we just show a confirmation message

        if (subscriptionId) {
            // Give webhook time to process
            setTimeout(() => {
                setStatus('success');
                setMessage('Votre paiement a été traité avec succès. Votre abonnement est maintenant actif.');
            }, 2000);
        } else {
            setStatus('error');
            setMessage('Aucune information de paiement trouvée.');
        }
    }, [searchParams]);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <Header onLogin={onLogin} />

            <main className="flex-1 flex items-center justify-center px-4 py-16">
                <div className="max-w-md w-full text-center">
                    {status === 'loading' && (
                        <div className="space-y-6">
                            <div className="w-20 h-20 mx-auto bg-cabaza-purple/10 rounded-full flex items-center justify-center">
                                <Loader2 className="w-10 h-10 text-cabaza-purple animate-spin" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                    Traitement en cours...
                                </h1>
                                <p className="text-gray-600">
                                    Veuillez patienter pendant que nous vérifions votre paiement.
                                </p>
                            </div>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="space-y-6">
                            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-10 h-10 text-green-600" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                    Paiement réussi !
                                </h1>
                                <p className="text-gray-600 mb-6">
                                    {message}
                                </p>
                            </div>
                            <div className="space-y-3">
                                <Link
                                    to="/"
                                    className="inline-flex items-center gap-2 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
                                >
                                    Accéder à mon espace
                                    <ArrowRight className="w-5 h-5" />
                                </Link>
                                <p className="text-sm text-gray-500">
                                    Un email de confirmation vous a été envoyé.
                                </p>
                            </div>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="space-y-6">
                            <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                                <XCircle className="w-10 h-10 text-red-600" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                    Paiement échoué
                                </h1>
                                <p className="text-gray-600 mb-6">
                                    {message || 'Une erreur est survenue lors du traitement de votre paiement.'}
                                </p>
                            </div>
                            <div className="space-y-3">
                                <Link
                                    to="/tarification"
                                    className="inline-flex items-center gap-2 bg-linear-to-r from-cabaza-purple to-cabaza-pink text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
                                >
                                    Réessayer
                                    <ArrowRight className="w-5 h-5" />
                                </Link>
                                <p className="text-sm text-gray-500">
                                    Si le problème persiste, contactez notre support.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default PaymentCallback;
